import { Link } from 'react-router-dom';
import type { Permission } from '@hrms/shared';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';

const MS_PER_DAY = 86_400_000;
/** The last few days of the trial switch from the calm style to the warning style. */
const WARN_WITHIN_DAYS = 3;

/**
 * Who sees the banner: only people who can act on it (choose a plan, finish the company details,
 * or read the agreement). A plain employee cannot do anything about the trial, so it is hidden.
 */
const ACTION_PERMISSIONS: readonly Permission[] = [
  'org.billing:read',
  'org.settings:update',
  'agreement:read',
];

type TrialStage = 'active' | 'ending' | 'ended';

function trialStage(daysLeft: number): TrialStage {
  if (daysLeft <= 0) return 'ended';
  if (daysLeft <= WARN_WITHIN_DAYS) return 'ending';
  return 'active';
}

const STAGE_CLASS: Record<TrialStage, string> = {
  active: 'border-accent/30 bg-accent-subtle text-accent',
  ending: 'border-warning/30 bg-warning-subtle text-warning',
  ended: 'border-danger/30 bg-danger-subtle text-danger',
};

const LINK_CLASS =
  'inline-flex min-h-11 items-center font-medium underline hover:no-underline sm:min-h-0';

function dayWord(n: number): string {
  return `${n} ${n === 1 ? 'day' : 'days'}`;
}

/**
 * The free-trial banner, shown for the whole 14-day trial until the agreement is signed. Every
 * new company starts on a trial with all modules open; when it ends the workspace becomes
 * read-only until a plan is chosen.
 */
export function TrialBanner() {
  const { me, permissions } = useAuthenticatedSession();
  const { trialEndsAt, agreementState } = me;
  // The API's own answer (ended trial or a billing lock) wins over the date maths below.
  const readOnly = me.readOnly === true;
  if (!readOnly && (!trialEndsAt || agreementState === 'signed')) return null;
  if (!ACTION_PERMISSIONS.some((p) => permissions.has(p))) return null;

  const endsAt = new Date(trialEndsAt ?? Number.NaN);
  const ends = endsAt.getTime();
  if (Number.isNaN(ends) && !readOnly) return null;
  const daysLeft = Number.isNaN(ends) ? 0 : Math.ceil((ends - Date.now()) / MS_PER_DAY);
  const stage: TrialStage = readOnly ? 'ended' : trialStage(daysLeft);
  // Locked by billing rather than by the trial running out (no trial, or one still running).
  const lockedByBilling = readOnly && (Number.isNaN(ends) || daysLeft > 0);

  // The viewer's own locale and timezone: the instant is the same everywhere, the label is theirs.
  const endDate = endsAt.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const message = lockedByBilling
    ? 'Your workspace is read-only: you can see everything but not change it. Choose a plan to continue.'
    : stage === 'ended'
      ? 'Your free trial has ended. Your workspace is read-only: you can see everything but not change it.'
      : stage === 'ending'
        ? `Your free trial ends in ${dayWord(daysLeft)} (on ${endDate}). After that your workspace becomes read-only.`
        : `Free trial: ${dayWord(daysLeft)} left. Every module is open while you try PeopleOrbit.`;

  // Plans & Pricing is the dedicated place to pick a plan, and billing stays usable while the
  // workspace is read-only. Someone who can edit the company but not see billing (a custom role)
  // falls back to the company details page.
  const planTarget = permissions.has('org.billing:read')
    ? '/admin/billing/plans'
    : permissions.has('org.settings:update')
      ? '/admin/company-details'
      : null;

  return (
    <div
      role="status"
      data-stage={stage}
      className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b px-4 py-2 text-sm sm:px-6 ${STAGE_CLASS[stage]}`}
    >
      <span className="min-w-0 flex-1 basis-64 break-words">{message}</span>
      <span className="flex flex-wrap items-center gap-x-4">
        {planTarget && (
          <Link to={planTarget} className={LINK_CLASS}>
            Choose a plan
          </Link>
        )}
        {permissions.has('agreement:read') && (
          <Link to="/admin/agreements" className={LINK_CLASS}>
            View agreement
          </Link>
        )}
      </span>
    </div>
  );
}
